import { Injectable } from '@angular/core';
import Swal, { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';
import { SessionStateService } from './session-state.service';

@Injectable({
  providedIn: 'root'
})
export class SweetAlertService {
  // Bootstrap colors
  private readonly primaryColor = '#0d6efd'; // Bootstrap primary color
  private readonly secondaryColor = '#6c757d'; // Bootstrap secondary color

  constructor(private sessionState: SessionStateService) { }

  /**
   * SweetAlert2 shows one popup at a time - firing a second one replaces the first and leaves its
   * promise hanging. Once the session is being torn down, the sign-out message must be the last
   * thing standing, so ordinary alerts are dropped rather than allowed to clobber it. Nine
   * components raise their own alert from an HTTP error handler, and a deactivated account fails
   * every request at once, so without this the user is told "something went wrong" instead of why.
   */
  private suppressed(): Promise<SweetAlertResult> | null {
    if (!this.sessionState.isTerminating) {
      return null;
    }

    return Promise.resolve({ isConfirmed: false, isDenied: false, isDismissed: true } as SweetAlertResult);
  }

  /** Bypasses the suppression above. For the sign-out message itself, nothing else. */
  fireCritical(options: SweetAlertOptions): Promise<SweetAlertResult> {
    return Swal.fire({ ...this.getDefaultConfig(), ...options });
  }


  private getDefaultConfig(): SweetAlertOptions {
    return {
      confirmButtonColor: this.primaryColor,
      cancelButtonColor: this.secondaryColor
    };
  }


  success(title: string, text?: string): Promise<SweetAlertResult> {
    const suppressed = this.suppressed();
    if (suppressed) return suppressed;

    return Swal.fire({
      title,
      text,
      icon: 'success',
      ...this.getDefaultConfig()
    });
  }


  error(title: string, text?: string): Promise<SweetAlertResult> {
    const suppressed = this.suppressed();
    if (suppressed) return suppressed;

    return Swal.fire({
      title,
      text,
      icon: 'error',
      ...this.getDefaultConfig()
    });
  }


  warning(title: string, text?: string): Promise<SweetAlertResult> {
    const suppressed = this.suppressed();
    if (suppressed) return suppressed;

    return Swal.fire({
      title,
      text,
      icon: 'warning',
      ...this.getDefaultConfig()
    });
  }


  info(title: string, text?: string): Promise<SweetAlertResult> {
    const suppressed = this.suppressed();
    if (suppressed) return suppressed;

    return Swal.fire({
      title,
      text,
      icon: 'info',
      ...this.getDefaultConfig()
    });
  }


  confirm(
    title: string,
    text?: string,
    confirmButtonText: string = 'Confirm',
    cancelButtonText: string = 'Cancel'
  ): Promise<SweetAlertResult> {
    const suppressed = this.suppressed();
    if (suppressed) return suppressed;

    return Swal.fire({
      title,
      text,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText,
      ...this.getDefaultConfig()
    });
  }


  confirmWarning(
    title: string,
    text?: string,
    confirmButtonText: string = 'Confirm',
    cancelButtonText: string = 'Cancel'
  ): Promise<SweetAlertResult> {
    const suppressed = this.suppressed();
    if (suppressed) return suppressed;

    return Swal.fire({
      title,
      text,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText,
      ...this.getDefaultConfig()
    });
  }


  fire(options: SweetAlertOptions): Promise<SweetAlertResult> {
    const suppressed = this.suppressed();
    if (suppressed) return suppressed;

    return Swal.fire({
      ...this.getDefaultConfig(),
      ...options
    });
  }
}
